import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { setModalState } from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import {
	LiveStreamConfirmationModalProps,
	LiveStreamStart,
} from '../modal/templates/live-stream-confirmation';
import {
	getDayHourMinSecFromStartTime,
	shouldShowStreamStartingAlert,
	withinUnsafeTimeLeft,
} from '../../utils/scheduled-streaming';
import { showAlert, showAlertLong } from '../general-components/alert-service';
import Socket from '../../connection/socket';
import colors from '../../utils/colors';

const StyledButton = styled.button`
	display: flex;
	justify-content: center;
	align-items: center;
	font-family: 'Roboto';
	padding: 6px 12px;
	gap: 6px;
	border-radius: 7.142rem;
	background: 
		linear-gradient(
			0deg,
			rgba(255, 255, 255, 0.20) 0%,
			rgba(255, 255, 255, 0.20) 100%
		), 
		linear-gradient(
			90deg,
			${colors.pinkLemonadePink} 0%,
			#55D6F0 100%
		);
`;

interface StreamBadgeProps {
	startTime: Date;
	socket: Socket | null;
	host: boolean;
}

export default function StreamBadge({ startTime, socket, host }: StreamBadgeProps) {
	const dispatch = useDispatch();

	const [now, setNow] = useState<number>(Date.now() / 1000);
	const timeout = useRef(undefined as undefined | NodeJS.Timeout);

	const countdown = useMemo(() => (startTime.valueOf() / 1000) - now, [now, startTime]);

	const durationString = useMemo(() => {
		const {
			day,
			hour,
			min,
			sec,
		} = getDayHourMinSecFromStartTime(startTime);

		if (day > 0) return `${day}d ${hour}h ${min}m`;
		if (hour > 0) return `${hour}h ${min}m ${sec}s`;
		return `${min}m ${sec}s`;
	}, [countdown]);

	useEffect(() => {
		if (timeout.current) {
			clearInterval(timeout.current);
		}

		timeout.current = setInterval(() => {
			setNow(Date.now() / 1000);
			if (shouldShowStreamStartingAlert(startTime)) {
				showAlert({
					message: 'Stream starting momentarily',
					description: 'You cannot make further changes',
					type: 'scheduled-stream-notification',
					duration: 7000,
				});
			}
		}, 1000);

		return () => {
			clearInterval(timeout.current);
		};
	}, [startTime]);

	const openStreamModal = () => {
		if (host && !withinUnsafeTimeLeft(startTime)) {
			dispatch(setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.LIVE_STREAM_CONFIRMATION,
				modalData: {
					socket,
					initialMode: countdown > 0
						? LiveStreamStart.Schedule
						: LiveStreamStart.StartNow,
				} as LiveStreamConfirmationModalProps,
			}));
		}
	};

	return (
		countdown > 0
			? (
				<StyledButton onClick={openStreamModal} style={{ cursor: host ? 'pointer' : 'default' }}>
					<Icon name={ICONS.TIMER} size={11} color={COLORS.TEXT_GRAY_1200} />
					<label className="bl-font-l2 grayscale-1200 white-space-nowrap pointer-events-none">
						{durationString}
					</label>
				</StyledButton>
			) : (
				<></>
			)
	);
}
